import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * @dynamic
 */
@Injectable({ providedIn: 'root' })
export class CopierService {
  private _fakeElement: HTMLTextAreaElement | null;

  private _document = inject(DOCUMENT);

  public copyText(text: string): boolean {
    try {
      this._createFake(text);

      return this._document.execCommand('copy');
    } catch {
      return false;
    } finally {
      this._removeFake();
    }
  }

  /**
   * Creates a fake textarea element, sets its value from `text` property,
   * and makes a selection on it.
   */
  public _createFake(text: string): void {
    const docElement = this._document.documentElement;
    const isRTL = docElement.getAttribute('dir') === 'rtl';

    // Create a fake element to hold the contents to copy
    this._fakeElement = this._document.createElement('textarea');

    // Prevent zooming on iOS
    this._fakeElement.style.fontSize = '12pt';

    // Reset box model
    this._fakeElement.style.border = '0';
    this._fakeElement.style.padding = '0';
    this._fakeElement.style.margin = '0';

    // Move element out of screen horizontally
    this._fakeElement.style.position = 'absolute';
    this._fakeElement.style[isRTL ? 'right' : 'left'] = '-9999px';

    // Move element to the same position vertically
    const yPosition = window.pageYOffset || docElement.scrollTop;
    this._fakeElement.style.top = yPosition + 'px';

    this._fakeElement.setAttribute('readonly', '');
    this._fakeElement.value = text;

    this._document.body.appendChild(this._fakeElement);

    this._fakeElement.select();
    this._fakeElement.setSelectionRange(0, this._fakeElement.value.length);
  }

  public _removeFake(): void {
    if (this._fakeElement) {
      this._document.body.removeChild(this._fakeElement);
      this._fakeElement = null;
    }
  }
}
